import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

import "./PlanManagement.css";
import api from "../../api/axios";
import { FiEye,FiEdit,FiTrash2 } from "react-icons/fi";
import {
  PREDEFINED_PLAN_FEATURES,
  OTHER_FEATURE_VALUE,
  ALLOWED_PLAN_DURATIONS,
} from "../../constants/planFeatures";


const PlanManagement = () => {
  const navigate = useNavigate();
  const modalRef = useRef(null);

  /* ================= STATES ================= */
  const [subscriptions, setSubscriptions] = useState([]);
  const [overAll,setOverAll]=useState(0)
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});

  const [newSubscription, setNewSubscription] = useState({
    _id: null,
    name: "",
    validity: "",
    unit: "",
    price: "",
    description: "",
    features: []
  });

  const [selectedFeatureOption, setSelectedFeatureOption] = useState("");
  const [customFeature, setCustomFeature] = useState("");

  // stats
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);

  // pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;


  // filters (UI ALREADY EXISTS)
  const [filters, setFilters] = useState({
    minDuration: "",
    maxDuration: "",
    minPrice: "",
    maxPrice: "",
    status: "",
    search: ""
  });

  // Reject anything that would make a non-negative numeric field (duration,
  // price) go negative. The `min="0"` attribute on the <input> only blocks
  // the spinner buttons, not a typed/pasted leading "-", so we also guard
  // onChange.
  const isNonNegativeNumberInput = (value) =>
    value === "" || (!value.includes("-") && !Number.isNaN(Number(value)));

  /* ================= API ================= */

  const fetchPlans = async () => {
    try {
      const res = await api.get("/admin/plans", {
        params: {
          ...filters,
          page,
          limit
        }
      });

      const plans = res.data?.data || [];

      setSubscriptions(plans);
      setOverAll(res.data?.alloverTotalRec)
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setTotalCount(res.data?.pagination?.totalRecords || 0);

      // active subscription count
      const activePlans = res.data?.activePlans
      setActiveCount(activePlans);
    } catch (err) {
      console.error("Fetch plans error:", err);
    }
  };

  const createPlan = async () => {
  if (!validateCreatePlan()) return;

  try {
    await api.post("/admin/plans", {
      name: newSubscription.name,
      price: Number(newSubscription.price),
      duration: newSubscription.validity,
      unit: newSubscription.unit,
      description: newSubscription.description,
      features: newSubscription.features
    });

    toast.success("Plan created successfully");
    closeModal();
    fetchPlans();
  } catch (err) {
    alert(err.response?.data?.message || "Failed to create plan");
  }
};

  const validateCreatePlan = () => {
    const newErrors = {};

    if (!newSubscription.name.trim()) {
      newErrors.name = "Name is required";
    } else if (newSubscription.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (newSubscription.name.trim().length > 50) {
      newErrors.name = "Name cannot exceed 50 characters";
    }

    if (!newSubscription.description.trim()) {
      newErrors.description = "Description is required";
    } else if (newSubscription.description.trim().length < 2) {
      newErrors.description = "Description must be at least 2 characters";
    } else if (newSubscription.description.trim().length > 300) {
      newErrors.description = "Description cannot exceed 300 characters";
    }

    if (!newSubscription.features.length) {
      newErrors.feature = "Add at least one feature from the list";
    }

    if (newSubscription.validity === "" || Number(newSubscription.validity) <= 0) {
      newErrors.validity = "Validity must be a positive number";
    }

    if (newSubscription.price === "" || Number(newSubscription.price) < 0) {
      newErrors.price = "Price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateUpdatePlan = () => {
    const newErrors = {};

    if (!newSubscription.name.trim()) {
      newErrors.name = "Name is required";
    } else if (newSubscription.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (newSubscription.name.trim().length > 50) {
      newErrors.name = "Name cannot exceed 50 characters";
    }

    if (!newSubscription.description.trim()) {
      newErrors.description = "Description is required";
    } else if (newSubscription.description.trim().length < 2) {
      newErrors.description = "Description must be at least 2 characters";
    } else if (newSubscription.description.trim().length > 300) {
      newErrors.description = "Description cannot exceed 300 characters";
    }

    // No feature input validation on update — existing features are preserved as-is

    if (newSubscription.validity === "" || Number(newSubscription.validity) <= 0) {
      newErrors.validity = "Validity must be a positive number";
    }

    if (newSubscription.price === "" || Number(newSubscription.price) < 0) {
      newErrors.price = "Price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
 const updatePlan = async () => {
  if (!validateUpdatePlan()) return;

  try {
    await api.put(`/admin/plans/${newSubscription._id}`, {
      name: newSubscription.name.trim(),
      price: Number(newSubscription.price),
      duration: newSubscription.validity,
      unit: newSubscription.unit,
      description: newSubscription.description.trim(),
      features: newSubscription.features,
      status: newSubscription.status
    });

    toast.success("Plan updated successfully");

    closeModal();
    fetchPlans();
  } catch (err) {
    toast.error(
      err.response?.data?.message || "Failed to update plan"
    );
  }
};

  const deletePlan = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscription?"))
      return;

    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Plan deleted successfully");
      fetchPlans();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  /* ================= HELPERS ================= */

  const handleView = (plan) => {
    navigate(`/plan-detail/${plan._id}`);
  };

  const handleUpdate = (plan) => {
    setIsEdit(true);
    setNewSubscription({
      _id: plan._id,
      name: plan.name,
      validity: plan.duration,
      unit: plan.unit || "",
      price: plan.price,
      description: plan.description || "",
      features: plan.features || [],
      status: plan.status || "active",
    });
    setSelectedFeatureOption("");
    setCustomFeature("");
    setErrors({});
    setShowAddModal(true);
  };

  const availableFeatureOptions = PREDEFINED_PLAN_FEATURES.filter(
    (feature) =>
      !newSubscription.features.some(
        (f) => f.trim().toLowerCase() === feature.toLowerCase()
      )
  );

  const resetFeaturePicker = () => {
    setSelectedFeatureOption("");
    setCustomFeature("");
    setErrors((prev) => ({ ...prev, feature: "" }));
  };

 const handleAddFeature = () => {
  let feature = "";

  if (selectedFeatureOption === OTHER_FEATURE_VALUE) {
    feature = customFeature.trim();
    if (!feature) {
      setErrors({ ...errors, feature: "Enter a custom feature" });
      return;
    }
  } else if (selectedFeatureOption) {
    feature = selectedFeatureOption;
  } else {
    setErrors({ ...errors, feature: "Select a feature from the list" });
    return;
  }

  if (feature.length < 2) {
    setErrors({ ...errors, feature: "Feature must be at least 2 characters" });
    return;
  }

  if (feature.length > 80) {
    setErrors({ ...errors, feature: "Feature cannot exceed 80 characters" });
    return;
  }

  const isDuplicate = newSubscription.features.some(
    (f) => f.trim().toLowerCase() === feature.toLowerCase()
  );
  if (isDuplicate) {
    setErrors({ ...errors, feature: "This feature is already added" });
    return;
  }

  setNewSubscription({
    ...newSubscription,
    features: [...newSubscription.features, feature],
  });

  resetFeaturePicker();
};
  const handleRemoveFeature = (index) => {
    setNewSubscription({
      ...newSubscription,
      features: newSubscription.features.filter((_, i) => i !== index)
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setNewSubscription({
      _id: null,
      name: "",
      validity: "",
      unit: "",
      price: "",
      description: "",
      features: []
    });
    setSelectedFeatureOption("");
    setCustomFeature("");
    setErrors({});
  };

  /* ================= EFFECT ================= */


  useEffect(() => {
  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeModal(); // close modal if click is outside
    }
  };

  if (showAddModal) {
    document.addEventListener("mousedown", handleClickOutside);
  } else {
    document.removeEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [showAddModal]);


  useEffect(() => {
    fetchPlans();
  }, [page, filters]);

  /* ================= UI (UNCHANGED) ================= */

  return (
    <div className="plan-management">
      <div className="plan-content">

         <div className="plan-header">
    <h1>Plan Management</h1>
  </div>

        {/* STATS */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-number">{overAll}</div>
            <div className="stat-label">Total Subscription</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{activeCount}</div>
            <div className="stat-label">Active Subscription</div>
          </div>
        </div>

        {/* TABLE SECTION */}
        <div className="subscription-section">
          <div className="section-header">
            <h3>Subscription Table</h3>
            <button
              className="add-btn"
              onClick={() => setShowAddModal(true)}
            >
              Add Subscription
            </button>
          </div>

          {/* FILTERS (ALREADY IN UI) */}
          <div className="table-filters">
            <input
              placeholder="Search"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <input
    type="number"
    min="0"
    placeholder="Min Duration"
    value={filters.minDuration}
    onChange={(e) => {
      if (!isNonNegativeNumberInput(e.target.value)) return;
      setFilters({ ...filters, minDuration: e.target.value });
    }}
  />
  <input
    type="number"
    min="0"
    placeholder="Max Duration"
    value={filters.maxDuration}
    onChange={(e) => {
      if (!isNonNegativeNumberInput(e.target.value)) return;
      setFilters({ ...filters, maxDuration: e.target.value });
    }}
  />
  <input
    type="number"
    min="0"
    placeholder="Min Price"
    value={filters.minPrice}
    onChange={(e) => {
      if (!isNonNegativeNumberInput(e.target.value)) return;
      setFilters({ ...filters, minPrice: e.target.value });
    }}
  />
  <input
    type="number"
    min="0"
    placeholder="Max Price"
    value={filters.maxPrice}
    onChange={(e) => {
      if (!isNonNegativeNumberInput(e.target.value)) return;
      setFilters({ ...filters, maxPrice: e.target.value });
    }}
  />
          </div>

          <div className="subscription-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subscription ID</th>
                  <th>Validity</th>
                   <th>Features</th>
                  <th>Price (₹)</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {subscriptions.map((sub) => (
                  <tr key={sub._id}>
                    <td>{sub.name}</td>
                    <td>{sub.planId}</td>
                    <td>{sub.duration} {sub.unit}</td>
                    <td>{sub.features?.join(', ') || 'N/A'}</td>
                    <td>₹{sub.price}</td>
                    <td>
<span className={`status ${sub.status}`}>
  {sub?.status
    ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1)
    : 'N/A'}
</span>
                    </td>
                    <td>
                      <div className="action-icons">
                        <button
                          className="icon-btn view"
                          onClick={() => handleView(sub)}
                        >
                          <FiEye className="fieye" />
                          
                        </button>
                        <button
                          className="icon-btn edit"
                          onClick={() => handleUpdate(sub)}
                        >
                        <FiEdit className="fiedit"/>
                        </button>
                        <button
                          className="icon-btn delete"
                          onClick={() => deletePlan(sub._id)}
                        >
                          <FiTrash2 className="fitrash"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="pagination">
            <button
              className={page === 1 ? "pagination-disabled" : "pagination-btn"}
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>

            <span>{page} / {totalPages}</span>

            <button
              className={
                page === totalPages
                  ? "pagination-disabled"
                  : "pagination-btn pag-nxt-btn"
              }
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* MODAL (UNCHANGED) */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal" ref={modalRef}>
            <h3>{isEdit ? "Update Subscription" : "Add Subscription"}</h3>

            <div className="form-group">
              <label>Name</label>
             <input
  value={newSubscription.name}
  onChange={(e) =>
    setNewSubscription({
      ...newSubscription,
      name: e.target.value.slice(0, 50)
    })
  }
/>

{errors.name && (
  <span className="form-error">{errors.name}</span>
)}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Validity</label>
                <input
                  type="number"
                  min="0"
                  value={newSubscription.validity}
                  onChange={(e) => {
                    if (!isNonNegativeNumberInput(e.target.value)) return;
                    setNewSubscription({
                      ...newSubscription,
                      validity: e.target.value
                    });
                  }}
                />
                {errors.validity && (
                  <span className="form-error">{errors.validity}</span>
                )}
              </div>

              <div className="form-group">
                <label>Unit</label>
                <select
                  value={newSubscription.unit}
                  onChange={(e) =>
                    setNewSubscription({
                      ...newSubscription,
                      unit: e.target.value
                    })
                  }
                >
                  <option value="">Select Unit</option>
                  <option value="month">Month (3 or 6 months)</option>
                  <option value="year">Year (1 year)</option>
                </select>
                <small style={{ color: '#6b7280', fontSize: 12 }}>
                  Allowed: {ALLOWED_PLAN_DURATIONS.map((d) => d.label).join(' · ')} — no weekly plans
                </small>
              </div>

              <div className="form-group">
                <label>Price(₹)</label>
                <input
                  type="number"
                  min="0"
                  value={newSubscription.price}
                  onChange={(e) => {
                    if (!isNonNegativeNumberInput(e.target.value)) return;
                    setNewSubscription({
                      ...newSubscription,
                      price: e.target.value
                    });
                  }}
                />
                {errors.price && (
                  <span className="form-error">{errors.price}</span>
                )}
              </div>
              
            </div>
            <div className="form-group">
  <label>Description</label>
 <textarea
  value={newSubscription.description}
  onChange={(e) =>
    setNewSubscription({
      ...newSubscription,
      description: e.target.value.slice(0, 300)
    })
  }
/>

{errors.description && (
  <span className="form-error">{errors.description}</span>
)}
</div>

     {isEdit && (
            <div className="form-group">
              <label>Status</label>
              <select
                value={newSubscription.status || ""}
                onChange={(e) =>
                  setNewSubscription({ ...newSubscription, status: e.target.value })
                }
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
     )}


            <div className="form-group">
              <label>Features</label>
              <div className="feature-input-container">
                <select
                  className="feature-select"
                  value={selectedFeatureOption}
                  onChange={(e) => {
                    setSelectedFeatureOption(e.target.value);
                    setErrors((prev) => ({ ...prev, feature: "" }));
                  }}
                >
                  <option value="">Select a feature</option>
                  {availableFeatureOptions.map((feature) => (
                    <option key={feature} value={feature}>
                      {feature}
                    </option>
                  ))}
                  <option value={OTHER_FEATURE_VALUE}>Other (custom)</option>
                </select>

                {selectedFeatureOption === OTHER_FEATURE_VALUE && (
                  <input
                    className="custom-feature-input"
                    value={customFeature}
                    onChange={(e) =>
                      setCustomFeature(e.target.value.slice(0, 80))
                    }
                    placeholder="Enter custom feature"
                  />
                )}

                <button
                  type="button"
                  className="add-feature-btn"
                  onClick={handleAddFeature}
                >
                  Add
                </button>
              </div>

              {errors.feature && (
                <span className="form-error">{errors.feature}</span>
              )}

              {newSubscription.features.map((f, i) => (
                <div key={`${f}-${i}`} className="feature-item">
                  <span>{f}</span>
                  <button
                    type="button"
                    className="remove-feature"
                    onClick={() => handleRemoveFeature(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={isEdit ? updatePlan : createPlan}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PlanManagement;
